import { useEffect, useRef } from "react";
import { useIdleTimer } from "react-idle-timer";

import { AUTH_SESSION_STARTED_AT } from "../constants/localStorageKeys";
import { safeStorage } from "../utils/storage";

interface IIdleTimeoutHookProps {
  onIdle: () => void;
  idleTime: number;
}

export const MAX_SESSION_HOURS = 12;
const MAX_SESSION_MS = MAX_SESSION_HOURS * 60 * 60 * 1000;

// Default events from react-idle-timer minus `visibilitychange` and `focus`.
// Why: returning to a backgrounded tab fires these, and the library treats them
// as activity — silently resetting the countdown instead of logging the user
// out after a long absence (EN-4893).
const IDLE_EVENTS = [
  "mousemove",
  "keydown",
  "wheel",
  "DOMMouseScroll",
  "mousewheel",
  "mousedown",
  "touchstart",
  "touchmove",
  "MSPointerDown",
  "MSPointerMove",
] as const;

export function useIdleTimeout({
  onIdle,
  idleTime = 1,
}: IIdleTimeoutHookProps) {
  const timeoutMs = 1000 * 60 * idleTime;
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const { getRemainingTime, getLastActiveTime, reset } = useIdleTimer({
    timeout: timeoutMs,
    onIdle: () => onIdleRef.current(),
    debounce: 500,
    events: [...IDLE_EVENTS],
    crossTab: true,
  });

  useEffect(() => {
    function sessionExceededCeiling(): boolean {
      const startedAtRaw = safeStorage.getItem(AUTH_SESSION_STARTED_AT);
      if (!startedAtRaw) return false;
      const startedAt = Number(startedAtRaw);
      if (!Number.isFinite(startedAt)) return false;
      return Date.now() - startedAt >= MAX_SESSION_MS;
    }

    function idleExceeded(): boolean {
      const lastActive = getLastActiveTime()?.getTime();
      if (!lastActive) return false;
      return Date.now() - lastActive >= timeoutMs;
    }

    function checkOnReturn() {
      if (document.visibilityState !== "visible") return;
      if (idleExceeded() || sessionExceededCeiling()) {
        onIdleRef.current();
        return;
      }
      reset();
    }

    document.addEventListener("visibilitychange", checkOnReturn);
    window.addEventListener("focus", checkOnReturn);

    // Schedule the hard ceiling deterministically so it fires even if no
    // user activity ever happens (which would otherwise keep the idle timer
    // alive and prevent onIdle from running).
    const startedAtRaw = safeStorage.getItem(AUTH_SESSION_STARTED_AT);
    let ceilingTimer: ReturnType<typeof setTimeout> | undefined;
    if (startedAtRaw) {
      const remaining = Number(startedAtRaw) + MAX_SESSION_MS - Date.now();
      ceilingTimer = setTimeout(
        () => onIdleRef.current(),
        Math.max(remaining, 0)
      );
    }

    return () => {
      document.removeEventListener("visibilitychange", checkOnReturn);
      window.removeEventListener("focus", checkOnReturn);
      if (ceilingTimer) clearTimeout(ceilingTimer);
    };
  }, [timeoutMs, getLastActiveTime, reset]);

  return {
    getRemainingTime,
    getLastActiveTime,
  };
}
